import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { LaunchNavigator } from '@capgo/capacitor-launch-navigator';
import { Check } from '@mui/icons-material';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import moment from 'moment-timezone';
import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { latitudeFormatter, longitudeFormatter } from '@sophocles/utils';
import DetailRow from '@sophocles/views/sites/components/DetailRow';
import SiteMapBackground, { USE_MAP_BACKDROP } from '@sophocles/views/sites/components/StructureCard/SiteMapBackground';

/**
 * Returns true when there are any extra structure fields present in the
 * site snapshot's extra_fields dict, taking the per-field show toggle into
 * account when fieldVisibility is provided.
 *
 * @param {object|null|undefined} extraFields - site.extra_fields plain dict from selectSiteData
 * @param {import('immutable').OrderedMap|null} [fieldVisibility] - FE-local show toggle state
 * @returns {boolean}
 */
export function hasVisibleExtraFields(extraFields, fieldVisibility) {
  if (!extraFields) return false;
  const keys = Object.keys(extraFields);
  if (!fieldVisibility) return keys.length > 0;
  return keys.some(k => fieldVisibility.getIn([k, 'show'], false));
}

function StructureDetailsList({
  customer,
  siteGroup,
  site,
  extraFields,
  // siteSchema: sorted array of {id, label?, order, ...} from customer.preferences.site_schema.
  // Drives field labels and ordering. Falls back to KNOWN_FIELD_TITLES when empty.
  siteSchema = [],
  // fieldVisibility: Immutable OrderedMap from state.selectedStructure.extraStructureFields.
  // Each entry is {id, show, title}. When provided, only entries with show:true are rendered.
  fieldVisibility = null,
  weatherConditions = null,
  weatherFeatureEnabled = false,
  onUpdateLocation = null,
}) {
  const { t } = useTranslation();

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [showNavAppDialog, setShowNavAppDialog] = useState(false);
  const [availableNavApps, setAvailableNavApps] = useState([]);
  const [selectedNavApp, setSelectedNavApp] = useState(null);

  // Long press handling for navigation button
  const longPressTimer = useRef(null);
  const longPressTriggered = useRef(false);
  const LONG_PRESS_DURATION = 500; // ms
  const NAV_APP_STORAGE_KEY = 'preferredNavApp';

  const _handleNativeLocation = async () => {
    try {
      // Check and request permissions
      const status = await Geolocation.checkPermissions();
      console.debug('Permission status:', status);
      if (status.location !== 'granted' || status.coarseLocation !== 'granted') {
        const requestStatus = await Geolocation.requestPermissions();
        console.debug('Request status:', requestStatus);

        if (requestStatus.location !== 'granted' && requestStatus.coarseLocation !== 'granted') {
          console.error('Geolocation permission not granted.');
          return;
        }
      }

      // Get current position
      const position = await Geolocation.getCurrentPosition({
        timeout: 10000, // 10 seconds
        enableHighAccuracy: true,
      });

      let { latitude, longitude } = position.coords;
      latitude = parseFloat(latitude.toFixed(8));
      longitude = parseFloat(longitude.toFixed(8));
      onUpdateLocation(latitude, longitude);
    } catch (e) {
      console.error('Geolocation error:', e); // Log the error object for debugging
      if (e.code !== undefined) {
        switch (e.code) {
          case 1: // PERMISSION_DENIED
            console.error('User denied the request for Geolocation.');
            break;
          case 2: // POSITION_UNAVAILABLE
            console.error('Location information is unavailable.');
            break;
          case 3: // TIMEOUT
            console.error('The request to get user location timed out.');
            break;
          default:
            console.error('An unknown error occurred fetching location.');
            break;
        }
      }

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            const { latitude, longitude } = position.coords;
            const pushLat = parseFloat(latitude.toFixed(8));
            const pushLon = parseFloat(longitude.toFixed(8));
            onUpdateLocation(pushLat, pushLon);
          },
          console.error,
          {
            enableHighAccuracy: true,
            timeout: 5000, // Set a timeout to wait for high accuracy data
            maximumAge: 0, // Prevent cached data from being returned
          },
        );
      } else {
        console.error('An unknown error occurred fetching location.');
      }
    }
  };

  const handleGeolocation = async () => {
    setIsCheckingLocation(true);
    await _handleNativeLocation();
    setIsCheckingLocation(false);
  };

  const closeConfirmationDialog = () => {
    setShowConfirmationDialog(false);
  };

  const confirmAndHandleGeolocation = () => {
    setShowConfirmationDialog(false);
    handleGeolocation();
  };

  // Only support Apple Maps, Google Maps, and web browser
  const SUPPORTED_NAV_APPS = {
    apple_maps: 'Apple Maps',
    google_maps: 'Google Maps',
  };

  const launchNavApp = async (appName, lat, lon) => {
    if (appName === 'web_browser') {
      window.open(`https://maps.google.com/maps?q=${lat},${lon}`, '_blank');
      return;
    }

    try {
      await LaunchNavigator.navigate({
        destination: [lat, lon],
        app: appName,
      });
    } catch (error) {
      console.error('Error launching navigator:', error);
      window.open(`https://maps.google.com/maps?q=${lat},${lon}`, '_blank');
    }
  };

  const latitude = site?.latitude ?? 0;
  const longitude = site?.longitude ?? 0;

  const handleOpenNavigation = async () => {
    // If long press was triggered, don't do the normal click action
    if (longPressTriggered.current) {
      longPressTriggered.current = false;
      return;
    }

    const lat = parseFloat(latitude);
    const lon = parseFloat(longitude);
    const webUrl = `https://maps.google.com/maps?q=${lat},${lon}`;

    if (Capacitor.isNativePlatform()) {
      // Check for saved preference
      const savedApp = localStorage.getItem(NAV_APP_STORAGE_KEY);
      if (savedApp) {
        await launchNavApp(savedApp, lat, lon);
        return;
      }

      try {
        const result = await LaunchNavigator.getAvailableApps();
        const apps = result?.apps || result;
        // apps is an array of objects with {name, app, available}
        // Filter to only supported apps that are available
        const appList = Object.values(apps)
          .filter(appInfo => appInfo.available && SUPPORTED_NAV_APPS[appInfo.app])
          .map(appInfo => appInfo.app);

        // Always show dialog with native apps + browser option
        setAvailableNavApps(appList);
        // Auto-select if only one option (including browser)
        const totalOptions = appList.length + 1; // +1 for browser
        if (totalOptions === 1) {
          setSelectedNavApp('web_browser');
        } else if (appList.length === 1) {
          setSelectedNavApp(appList[0]);
        }
        setShowNavAppDialog(true);
      } catch (error) {
        console.error('Error launching navigator:', error);
        // Still show dialog with just browser option on error
        setAvailableNavApps([]);
        setSelectedNavApp('web_browser'); // Auto-select browser when it's the only option
        setShowNavAppDialog(true);
      }
    } else {
      window.open(webUrl, '_blank');
    }
  };

  const handleNavButtonTouchStart = async () => {
    longPressTriggered.current = false;
    longPressTimer.current = setTimeout(async () => {
      longPressTriggered.current = true;
      // Clear preference and show dialog
      localStorage.removeItem(NAV_APP_STORAGE_KEY);

      if (Capacitor.isNativePlatform()) {
        try {
          const result = await LaunchNavigator.getAvailableApps();
          const apps = result?.apps || result;
          const appList = Object.values(apps)
            .filter(appInfo => appInfo.available && SUPPORTED_NAV_APPS[appInfo.app])
            .map(appInfo => appInfo.app);
          setAvailableNavApps(appList);
          // Auto-select if only one option (including browser)
          const totalOptions = appList.length + 1; // +1 for browser
          if (totalOptions === 1) {
            setSelectedNavApp('web_browser');
          } else if (appList.length === 1) {
            setSelectedNavApp(appList[0]);
          }
          setShowNavAppDialog(true);
        } catch (error) {
          console.error('Error launching navigator:', error);
          setAvailableNavApps([]);
          setSelectedNavApp('web_browser'); // Auto-select browser when it's the only option
          setShowNavAppDialog(true);
        }
      }
    }, LONG_PRESS_DURATION);
  };

  const handleNavButtonTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleRememberAndOpen = async () => {
    if (!selectedNavApp) return;
    setShowNavAppDialog(false);
    const lat = parseFloat(latitude);
    const lon = parseFloat(longitude);
    localStorage.setItem(NAV_APP_STORAGE_KEY, selectedNavApp);
    await launchNavApp(selectedNavApp, lat, lon);
    setSelectedNavApp(null);
  };

  const getNavAppDisplayName = appName => {
    if (appName === 'web_browser') return 'Web Browser';
    return SUPPORTED_NAV_APPS[appName] || appName;
  };

  const formattedLatitude = latitudeFormatter(latitude);
  const formattedLongitude = longitudeFormatter(longitude);
  const location = site?.location_description ?? '';
  const siteTimeZone = site?.time_zone ?? '';
  const tzMoment = siteTimeZone ? moment().tz(siteTimeZone) : null;
  const zoneName = tzMoment ? tzMoment.zoneName() : null;
  const timezone = zoneName ?? 'N/A';

  // Static title lookup for known extra-field ids — used as fallback when
  // the customer has no site_schema configured. User fields (user_*) fall
  // back to their key (without the user_ prefix) as the display title.
  const KNOWN_FIELD_TITLES = {
    tag_number: t('sites.fieldSecurityTag', 'Security Tag'),
    utility_meter_number: t('sites.meterNumber', 'Meter Number'),
    utility_company: t('sites.utilityCompany', 'Utility Company'),
    utility_account_number: t('sites.utilityAccount', 'Utility Account'),
  };

  // Build a lookup from field id → schema entry for O(1) label/order access.
  // siteSchema is normally the sorted array from customerPreferenceSelector
  // ([{id, label?, order, ...}]), but it can also arrive as the raw
  // { fieldKey: {label, order, ...} } dict (or absent). Normalize to a list so
  // a non-array shape never breaks the card. KNOWN_FIELD_TITLES and the
  // key-name fallback apply when no schema entry is found.
  const schemaList = Array.isArray(siteSchema)
    ? siteSchema
    : siteSchema && typeof siteSchema === 'object'
      ? Object.entries(siteSchema).map(([id, entry]) => ({ id, ...(entry && typeof entry === 'object' ? entry : {}) }))
      : [];
  const schemaById = Object.fromEntries(schemaList.map(entry => [entry.id, entry]));

  // Resolve title for a field id: schema label → static map → key fallback.
  const resolveTitle = id => {
    const schemaEntry = schemaById[id];
    if (schemaEntry?.label) return schemaEntry.label;
    return KNOWN_FIELD_TITLES[id] ?? (id.startsWith('user_') ? id.slice('user_'.length) : id);
  };

  // Resolve sort order for a field id from the schema (default: Infinity to
  // preserve original entry order for fields not in the schema).
  const resolveOrder = id => schemaById[id]?.order ?? Infinity;

  // Build a plain-JS array of { id, title, value } from site.extra_fields,
  // filtered by the FE per-field show toggle when fieldVisibility is provided,
  // and sorted by the customer's site_schema order.
  const extraFieldsDict = extraFields ?? {};
  const extraFieldArray = Object.entries(extraFieldsDict)
    .filter(([id]) => {
      if (!fieldVisibility) return true;
      return fieldVisibility.getIn([id, 'show'], false);
    })
    .map(([id, value]) => ({
      id,
      title: resolveTitle(id),
      value: value ?? '',
      sortKey: resolveOrder(id),
    }))
    .sort((a, b) => a.sortKey - b.sortKey)
    .map(({ sortKey, ...field }) => field);

  const normalizeExtraFieldTitle = field =>
    String(field.title ?? '')
      .trim()
      .toLowerCase();
  const getExtraFieldId = field => String(field.id ?? '').trim();
  const isUtilityCompanyField = field => {
    const id = getExtraFieldId(field);
    const title = normalizeExtraFieldTitle(field);

    return id === 'utility_company' || (title.includes('utility') && title.includes('company'));
  };
  const isUtilityAccountField = field => {
    const id = getExtraFieldId(field);
    const title = normalizeExtraFieldTitle(field);

    return (
      id === 'utility_account_number' ||
      title === 'account' ||
      title === 'account number' ||
      (title.includes('utility') && title.includes('account'))
    );
  };
  const utilityPairFields = [
    extraFieldArray.find(isUtilityCompanyField),
    extraFieldArray.find(isUtilityAccountField),
  ].filter(Boolean);
  const groupedExtraFieldTitles = new Set([
    'account',
    'account number',
    'meter number',
    'security tag',
    'security tag #',
    'security tag number',
    'utility account',
    'utility account #',
    'utility account number',
    'utility company',
    'utility meter number',
  ]);
  const isGroupedExtraField = field => groupedExtraFieldTitles.has(normalizeExtraFieldTitle(field));
  const getGroupedExtraFieldOrder = field => {
    const title = normalizeExtraFieldTitle(field);

    if (title === 'utility company') return 0;
    if (
      title === 'account' ||
      title === 'account number' ||
      title === 'utility account' ||
      title === 'utility account #' ||
      title === 'utility account number'
    ) {
      return 1;
    }
    if (title === 'security tag' || title === 'security tag #' || title === 'security tag number') return 2;
    if (title === 'meter number' || title === 'utility meter number') return 3;

    return 4;
  };
  const groupedExtraFields = extraFieldArray
    .filter(field => !utilityPairFields.includes(field) && isGroupedExtraField(field))
    .sort((a, b) => {
      const orderDelta = getGroupedExtraFieldOrder(a) - getGroupedExtraFieldOrder(b);

      return orderDelta !== 0 ? orderDelta : extraFieldArray.indexOf(a) - extraFieldArray.indexOf(b);
    });
  const standardExtraFields = extraFieldArray.filter(
    field => !utilityPairFields.includes(field) && !isGroupedExtraField(field),
  );

  // Weather rides the Time Zone row's trailing action slot (row-aligned, like
  // the GPS row's map button): a compact Chip holding the OWM condition icon
  // and the darkness score. One Tooltip over the whole Chip surfaces the full
  // temp/condition description on hover.
  const showWeather = weatherConditions != null && weatherFeatureEnabled;
  const darknessScore = weatherConditions?.darkness_score;
  const weatherConditionsText = weatherConditions?.description ?? '';
  const weatherChip =
    showWeather && weatherConditions?.icon ? (
      <Tooltip title={weatherConditionsText}>
        <Chip
          size="small"
          variant="outlined"
          avatar={<Avatar src={weatherConditions.icon} alt="" variant="square" sx={{ width: 18, height: 18 }} />}
          label={darknessScore != null ? darknessScore : weatherConditionsText}
          sx={{ '& .MuiChip-avatar': { width: 18, height: 18, borderRadius: 0 } }}
        />
      </Tooltip>
    ) : null;

  return (
    <Box>
      <SiteMapBackground latitude={latitude} longitude={longitude}>
        <Box
          sx={{
            py: 'var(--space-1)',
            // Plain mode (no map glass panel) packs the rows a notch tighter —
            // the map layout's row height reads airy without the backdrop.
            ...(USE_MAP_BACKDROP
              ? {}
              : {
                  '& > div': {
                    minHeight: 36,
                    paddingTop: 'var(--space-1)',
                    paddingBottom: 'var(--space-1)',
                  },
                }),
          }}
        >
          <DetailRow id="site-location" label={t('general.location')} value={location} />
          <DetailRow label={t('general.timeZone')} value={timezone} action={weatherChip} />
          <DetailRow
            id="site-coordinates"
            label={t('sites.gpsCoordinates')}
            value={`${formattedLatitude}, ${formattedLongitude}`}
            action={
              <IconButton
                size="small"
                aria-label={t('sites.showOnGoogleMaps')}
                onClick={handleOpenNavigation}
                onTouchStart={handleNavButtonTouchStart}
                onTouchEnd={handleNavButtonTouchEnd}
                onMouseDown={handleNavButtonTouchStart}
                onMouseUp={handleNavButtonTouchEnd}
                onMouseLeave={handleNavButtonTouchEnd}
              >
                <MapOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </IconButton>
            }
          />
        </Box>
      </SiteMapBackground>

      <Box sx={{ py: 'var(--space-1)' }}>
        {utilityPairFields.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 'var(--space-2)',
              px: 'var(--space-3)',
              pt: 'var(--space-2)',
              pb: groupedExtraFields.length > 0 ? 0 : 'var(--space-2)',
            }}
          >
            {utilityPairFields.map(field => (
              <DetailRow key={field.id} dense label={field.title} value={field.value} />
            ))}
          </Box>
        ) : null}
        {groupedExtraFields.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 'var(--space-2)',
              px: 'var(--space-3)',
              py: 'var(--space-2)',
            }}
          >
            {groupedExtraFields.map(field => (
              <DetailRow key={field.id} dense label={field.title} value={field.value} />
            ))}
          </Box>
        ) : null}
        {standardExtraFields.map(field => (
          <DetailRow key={field.id} label={field.title} value={field.value} />
        ))}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmationDialog} onClose={closeConfirmationDialog}>
        <DialogTitle>{t('sites.confirmGpsUpdate')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('sites.confirmGpsUpdateMessage')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmationDialog} color="primary">
            {t('actions.cancel')}
          </Button>
          <Button onClick={confirmAndHandleGeolocation} color="primary" autoFocus>
            {t('actions.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Navigation App Selection Dialog */}
      <Dialog open={showNavAppDialog} onClose={() => setShowNavAppDialog(false)}>
        <DialogContent sx={{ p: 0, pt: 1 }}>
          <List sx={{ minWidth: 250 }}>
            {availableNavApps.map(appName => (
              <ListItemButton
                key={appName}
                selected={selectedNavApp === appName}
                onClick={() => setSelectedNavApp(appName)}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: selectedNavApp === appName ? 'primary.main' : 'grey.300' }}>
                    <Icon className="fas fa-map-marker-alt" />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={getNavAppDisplayName(appName)} />
                {selectedNavApp === appName && <Check color="primary" />}
              </ListItemButton>
            ))}
            <ListItemButton
              selected={selectedNavApp === 'web_browser'}
              onClick={() => setSelectedNavApp('web_browser')}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: selectedNavApp === 'web_browser' ? 'primary.main' : 'grey.300' }}>
                  <Icon className="fas fa-globe" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary={t('sites.openInBrowser')} />
              {selectedNavApp === 'web_browser' && <Check color="primary" />}
            </ListItemButton>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRememberAndOpen} disabled={!selectedNavApp} variant="contained">
            {t('actions.open')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default StructureDetailsList;
